import gql from "graphql-tag"
import moment from 'moment'

/**
 *
 * @class QuestionnaireManager
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.3] app/src/APP_DASHBOARD/manager/QuestionnaireManager.js
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export class QuestionnaireManager {
    constructor(
        apolloClient,
        apolloMobileClient,
        store
    ) {
        /** @type ApolloClient */
        this.apolloClient = apolloClient

        /** @type ApolloClient */
        this.apolloMobileClient = apolloMobileClient

        this.configurationCache = null

        this._store = store
    }

    async getActivePatientQuestionnaire(patientId, questionnaireName, version = null) {
        const useVersion = version !== null;

        if (useVersion) {
            if (version === true) {
                version = this._store.getters['patientSummary/currentVersion']
            }
            if (!version) {
                throw new Error('Version missing')
            }
        }

        const questionnaires = await this.getPatientQuestionnaires(
            patientId,
            version,
            questionnaireName,
            null,
            null,
            1,
            false,
        )

        return questionnaires.length ? questionnaires[0] : null
    }

    async getPatientQuestionnaires(
        patientId,
        versions = null,
        questionnaireNames = null,
        startDate = null,
        endDate = null,
        limit = null,
        completedOnly = true,
        ascOrder = false
    ) {
        const limitString = limit !== null ? `limit : ${limit}` : ''

        const variables = {patientId}
        let variableString = "$patientId: String!,"
        let conditionString = ""
        let orderString = ascOrder ?
            `orders : {field : formEntryDate, direction : asc}` :
            `orders : {field : formEntryDate, direction : desc}`

        if (versions !== null) {
            if (!Array.isArray(versions)) versions = [versions]

            if (versions.length) {
                variableString += "$versions: [String!]!,"
                variables.versions = versions
                conditionString += '{ field: version, operator : in, parameters: $versions },'
            }
        }

        if (questionnaireNames !== null) {
            if (!Array.isArray(questionnaireNames)) questionnaireNames = [questionnaireNames]

            if (questionnaireNames.length) {
                variableString += "$questionnaireNames: [String!]!,"
                variables.questionnaireNames = questionnaireNames
                conditionString += '{ field: key, operator : in, parameters: $questionnaireNames },'
            }
        }

        if (startDate !== null) {
            if (startDate instanceof moment)
                startDate = startDate.format("YYYY-MM-DD HH:mm:ss")

            variableString += "$startDate: String!,"
            variables.startDate = startDate
            conditionString += '{field: formEntryDate, operator: gte, value : $startDate},'
        }

        if (endDate !== null) {
            if (endDate instanceof moment)
                endDate = endDate.format("YYYY-MM-DD HH:mm:ss")

            variableString += "$endDate: String!,"
            variables.endDate = endDate
            conditionString += '{field: formEntryDate, operator: lte, value : $endDate},'
        }

        if (completedOnly) {
            conditionString += '{field : completed, value : "1"},'
        }

        if (conditionString !== "")
            conditionString = `conditions : { gate : and, inputs : [${conditionString}]}`

        return this.apolloMobileClient
            .query({
                query: gql`query getPatientQuestionnaires(${variableString})
      {
          myQuestionnaireForms(
              functions: {function: patientId, parameters: [$patientId]}
              ${conditionString}
              ${orderString}
              ${limitString}
          ){
              questionnaire{
                  key
              }

              id
              completed
              score
              entryDate
              updatedAt
              entries{
                  id
                  question{
                      key
                      text
                      sequence
                  }
                  givenAnswers{
                      id
                      key
                      score
                      text
                      openAnswer
                      openAnswerObject
                  }
              }

          }
      }
      `,
                variables: variables,
            }).then(result => {
                return result.data.myQuestionnaireForms
            })
    }

    async getSmokingHistory (patientId) {
        return this.apolloMobileClient
            .query({
                query: gql`query mySmokingHistory($patientId: String!
                ) {
                    mySmokingHistory(
                        functions: { function: patientId, parameters: [$patientId] },
                        orders : [{ field : entryDate, direction : asc }]
                    ){
                        answer
                        mappedAnswer
                        entryDate
                        questionnaireSource
                    }
                }`, variables: { patientId }
            }).then(result => {
                return result.data.mySmokingHistory
            })
    }

    async getEProConfigurationCollection(subjectId, collectionKey, includeQuestionnaireDetailInformation = false) {
        const extraInformation = includeQuestionnaireDetailInformation ? '' +
            `questionnaires{
                  key
                  name
                  internalName
                }
            forms{
              allRequiredQuestionsCompleted
              questionnaire{
                key
              }
             }
            ` : '';

        return this.apolloClient
            .query({
                query: gql`query getEProConfigurationCollection($subjectId : String!, $collectionKey : String!)
            {
              eProConfigurationCollection(
                subjectId : $subjectId
                collectionKey : $collectionKey
              ){
                hasQuestionnaires
                questionnaireKeys
                routes
                remainingQuestionnaireKeys
                requiredQuestionnaireKeys
                hasRemainingQuestionnairesToBeFilled
                ${extraInformation}
              }
            }
      `,
                variables: {subjectId, collectionKey},
            }).then(result => {
                return result.data.eProConfigurationCollection
            })
    }
}